import React, { useState } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Card,
  Grid,
  TextField,
  Skeleton,
  Alert,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import InventoryIcon from '@mui/icons-material/Inventory2Outlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { palette } from '../../theme/colors';

interface B2BProduct {
  id: string;
  name: string;
  description: string;
  detailedDescription?: string;
  category: string;
  sku: string;
  basePrice: number;
  finalPrice: number;
  discountAmount: number;
  b2bMinimumQuantity?: number;
  images: Array<{ thumbnail: string; url: string }>;
}

interface PortalContext {
  portalInfo: {
    organization: { id: string; name: string };
  };
  isAuthenticated: boolean;
}

const getAuthToken = (): string | null => {
  return localStorage.getItem('access_token');
};

const fetchB2BProduct = async (orgSlug: string, productId: string, token: string): Promise<B2BProduct> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/b2b-portal/${orgSlug}/products/${productId}`,
    {
      headers: { 'Authorization': `Bearer ${token}` },
    }
  );
  if (!response.ok) {
    throw new Error('Failed to fetch product');
  }
  const data = await response.json();

  // Handle nested product structure from API
  if (data.product) {
    return {
      id: data.product.id,
      name: data.product.name,
      description: data.product.description,
      detailedDescription: data.product.detailedDescription,
      category: data.product.category,
      sku: data.product.sku,
      basePrice: data.originalPrice,
      finalPrice: data.finalPrice,
      discountAmount: data.originalPrice - data.finalPrice,
      b2bMinimumQuantity: data.product.b2bMinimumQuantity,
      images: data.product.images || [],
    };
  }
  return data;
};

const createQuoteRequest = async (
  orgSlug: string,
  token: string,
  data: { items: Array<{ productId: string; quantity: number }>; message?: string }
) => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/b2b-portal/${orgSlug}/quote-request`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create quote request');
  }
  return response.json();
};

export const B2BProductDetailPage: React.FC = () => {
  const { orgSlug, productId } = useParams<{ orgSlug: string; productId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useOutletContext<PortalContext>();
  const token = getAuthToken();

  const [quantity, setQuantity] = useState(1);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [quoteNotes, setQuoteNotes] = useState('');
  const [quoteSuccess, setQuoteSuccess] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [cartSnackbarOpen, setCartSnackbarOpen] = useState(false);

  // Redirect if not authenticated
  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate(`/b2b/${orgSlug}/login`);
    }
  }, [isAuthenticated, navigate, orgSlug]);

  const { data: product, isLoading, error } = useQuery({
    queryKey: ['b2b-product', orgSlug, productId],
    queryFn: () => fetchB2BProduct(orgSlug!, productId!, token!),
    enabled: !!orgSlug && !!productId && !!token,
  });

  const quoteMutation = useMutation({
    mutationFn: (data: { items: Array<{ productId: string; quantity: number }>; message?: string }) =>
      createQuoteRequest(orgSlug!, token!, data),
    onSuccess: () => {
      setQuoteSuccess(true);
    },
  });

  // Minimum quantity validation
  const minQuantity = product?.b2bMinimumQuantity || 1;
  const isBelowMinimum = quantity < minQuantity;

  // Set initial quantity based on minimum requirement
  React.useEffect(() => {
    if (product?.b2bMinimumQuantity && product.b2bMinimumQuantity > 1) {
      setQuantity(product.b2bMinimumQuantity);
    }
  }, [product?.b2bMinimumQuantity]);

  const handleAddToCart = () => {
    if (!product || isBelowMinimum) return;

    const cartStr = localStorage.getItem('b2b_cart');
    const cart = cartStr ? JSON.parse(cartStr) : [];

    const existingIndex = cart.findIndex((item: any) => item.productId === product.id);
    if (existingIndex >= 0) {
      cart[existingIndex].quantity += quantity;
    } else {
      cart.push({
        productId: product.id,
        name: product.name,
        price: product.finalPrice,
        quantity,
        image: product.images?.[0]?.thumbnail,
        minQuantity: minQuantity,
      });
    }

    localStorage.setItem('b2b_cart', JSON.stringify(cart));
    setCartSnackbarOpen(true);
  };

  const handleRequestQuote = () => {
    quoteMutation.mutate({
      items: [{ productId: productId!, quantity }],
      message: quoteNotes || undefined,
    });
  };

  const getImageUrl = (path?: string) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${path}`;
  };

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rounded" height={400} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="text" height={60} />
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" height={100} sx={{ mt: 2 }} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error || !product) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/b2b/${orgSlug}/products`)}
          sx={{ mb: 3 }}
        >
          Back to Products
        </Button>
        <Alert severity="error">Product not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/b2b/${orgSlug}/products`)}
        sx={{ mb: 3, color: palette.textSecondary }}
      >
        Back to Products
      </Button>

      <Grid container spacing={4}>
        {/* Images */}
        <Grid item xs={12} md={6}>
          <Card sx={{ overflow: 'hidden' }}>
            <Box
              sx={{
                height: 400,
                bgcolor: palette.sky,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {product.images && product.images.length > 0 ? (
                <Box
                  component="img"
                  src={getImageUrl(product.images[selectedImage]?.url || product.images[selectedImage]?.thumbnail)}
                  alt={product.name}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    p: 4,
                  }}
                />
              ) : (
                <InventoryIcon sx={{ fontSize: 120, color: palette.textMuted }} />
              )}
            </Box>
            {product.images && product.images.length > 1 && (
              <Box sx={{ display: 'flex', gap: 1, p: 2 }}>
                {product.images.map((img, idx) => (
                  <Box
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: `2px solid ${idx === selectedImage ? palette.primary : palette.border}`,
                      cursor: 'pointer',
                    }}
                  >
                    <Box
                      component="img"
                      src={getImageUrl(img.thumbnail)}
                      alt=""
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Card>
        </Grid>

        {/* Details */}
        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            {product.category && (
              <Chip label={product.category} size="small" sx={{ bgcolor: palette.sky }} />
            )}
            {product.discountAmount > 0 && (
              <Chip
                label="B2B Pricing"
                size="small"
                sx={{ bgcolor: palette.success, color: '#fff' }}
              />
            )}
          </Box>

          <Typography variant="h4" fontWeight={700} gutterBottom>
            {product.name}
          </Typography>

          {product.sku && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              SKU: {product.sku}
            </Typography>
          )}

          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {product.description || 'No description available'}
          </Typography>

          {/* Detailed Description (HTML) */}
          {product.detailedDescription && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: palette.background || '#f9fafb',
                borderRadius: 1,
                '& h1, & h2, & h3': { mt: 2, mb: 1 },
                '& p': { mb: 1 },
                '& ul, & ol': { pl: 2, mb: 1 },
                '& li': { mb: 0.5 },
              }}
              dangerouslySetInnerHTML={{ __html: product.detailedDescription }}
            />
          )}

          <Divider sx={{ my: 3 }} />

          {/* Price */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" fontWeight={800} color={palette.primary}>
              {product.finalPrice.toFixed(2)} DZD
            </Typography>
            {product.discountAmount > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Typography
                  variant="body1"
                  sx={{ textDecoration: 'line-through', color: palette.textMuted }}
                >
                  {product.basePrice.toFixed(2)} DZD
                </Typography>
                <Chip
                  label={`Save ${product.discountAmount.toFixed(2)} DZD`}
                  size="small"
                  sx={{ bgcolor: palette.successLight, color: palette.success }}
                />
              </Box>
            )}
          </Box>

          {/* Minimum Quantity Alert */}
          {minQuantity > 1 && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              icon={<InventoryIcon />}
            >
              Minimum order quantity: <strong>{minQuantity} units</strong>
            </Alert>
          )}

          {/* Quantity */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="body1" fontWeight={500}>
              Quantity:
            </Typography>
            <TextField
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(Math.max(minQuantity, parseInt(e.target.value) || minQuantity))}
              inputProps={{ min: minQuantity }}
              sx={{ width: 100 }}
              size="small"
              error={isBelowMinimum}
            />
            <Typography variant="body2" color="text.secondary">
              Total: {(product.finalPrice * quantity).toFixed(2)} DZD
            </Typography>
          </Box>

          {/* Below minimum warning */}
          {isBelowMinimum && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Please select at least {minQuantity} units to proceed.
            </Alert>
          )}

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              size="large"
              startIcon={<AddShoppingCartIcon />}
              onClick={handleAddToCart}
              disabled={isBelowMinimum}
              sx={{ flex: 1, py: 1.5, borderColor: palette.border }}
            >
              Add to Cart
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<RequestQuoteIcon />}
              onClick={() => setQuoteDialogOpen(true)}
              disabled={isBelowMinimum}
              sx={{ flex: 1, py: 1.5 }}
            >
              Request Quote
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Quote Dialog */}
      <Dialog open={quoteDialogOpen} onClose={() => !quoteSuccess && setQuoteDialogOpen(false)} maxWidth="sm" fullWidth>
        {quoteSuccess ? (
          <>
            <DialogContent sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: palette.success, mb: 2 }} />
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Quote Request Submitted!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                We'll review your request and get back to you shortly.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
              <Button
                variant="contained"
                onClick={() => {
                  setQuoteDialogOpen(false);
                  setQuoteSuccess(false);
                  setQuoteNotes('');
                }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>Request Quote for {product.name}</DialogTitle>
            <DialogContent>
              <Box sx={{ py: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Quantity: <strong>{quantity}</strong> × {product.finalPrice.toFixed(2)} DZD ={' '}
                  <strong>{(product.finalPrice * quantity).toFixed(2)} DZD</strong>
                </Typography>
                <TextField
                  label="Notes (Optional)"
                  multiline
                  rows={4}
                  fullWidth
                  value={quoteNotes}
                  onChange={(e) => setQuoteNotes(e.target.value)}
                  placeholder="Add any special requirements or questions..."
                />
              </Box>
              {quoteMutation.error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {quoteMutation.error instanceof Error ? quoteMutation.error.message : 'Failed to submit'}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setQuoteDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleRequestQuote}
                disabled={quoteMutation.isPending}
              >
                {quoteMutation.isPending ? 'Submitting...' : 'Submit Quote Request'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Add to Cart Snackbar */}
      <Snackbar
        open={cartSnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setCartSnackbarOpen(false)}
        message={`${quantity} × ${product?.name || 'Product'} added to cart`}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};
